let _ = t => t,
  _t;
import { useEffect, useRef, useState } from 'react';
import { NavMarker } from 'react-rhumb';
import styledWithTheme from 'foundations-theming/utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { H3 } from 'foundations-components/transitional/components/H3';
import { Button } from 'foundations-components/transitional/components/Button';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { Image } from 'foundations-components/transitional/components/Image';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Pictogram } from 'foundations-components/transitional/components/Pictogram';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { startConfetti } from 'academy-common-lib/utils/confetti';
import ApiError from '../../../components/Errors/ApiError';
import { ACADEMY_ONBOARDING_URL_FRAGMENT } from '../../../constants/Navigation.legacy';
import OnboardingSectionLayout from './OnboardingSectionLayout';
import { PageSectionHeading } from '../../../components/SectionHeading';
import { ACADEMY_ONBOARDING_SECTION_IDS } from '../constants/sectionAnchors';
import UsageTracker from '../../../utils/UsageTracker';
import { ClampedText } from 'academy-common-lib/components/card/shared';
import SessionDateDisplay from 'academy-common-lib/features/liveEvents/components/SessionDateDisplay';
import { ZoomRegistrationFallbackModal } from 'academy-common-lib/features/liveEvents/ZoomRegistrationFallbackModal';
import { RegistrationConfirmationModal } from 'academy-common-lib/features/liveEvents/RegistrationConfirmationModal';
import { useRegisterForSession } from 'academy-common-lib/features/liveEvents/liveEventQueries';
import { SkeletonBlock, SkeletonWrapper } from 'academy-common-lib/components/Skeletons/subcomponents/Skeleton';
import { getSessionEndTime, getSessionStartTime } from 'academy-common-lib/features/liveEvents/sessionTimeUtils';
import { useNextStepEvent } from 'academy-common-lib/features/liveEvents/nextStepUtils';
import { useOverviewNavigation } from 'academy-common-lib/features/overview/overviewRouting';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CONFETTI_SESSION_KEY = 'ao-next-step-pending-confetti';
const ThumbnailWrapper = styledWithTheme.div(_t || (_t = _`
  aspect-ratio: 11 / 6;
  inline-size: 100%;
`));
const NextStepCardSkeleton = ({
  isMobile,
  isTablet,
  isLaptop
}) => /*#__PURE__*/_jsx(CardWrapper, {
  borderRadius: "300",
  marginBlockEnd: "0",
  paddingBlock: "800",
  paddingInline: isLaptop ? '400' : '800',
  children: /*#__PURE__*/_jsxs(Flex, {
    alignItems: isMobile || isTablet ? 'stretch' : 'center',
    flexDirection: isMobile || isTablet ? 'column' : 'row',
    gap: "600",
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "start",
      blockSize: "100%",
      flex: "2 1 0",
      flexDirection: "column",
      gap: "300",
      justifyContent: "center",
      children: [/*#__PURE__*/_jsx(SkeletonWrapper, {
        borderRadius: "100",
        children: /*#__PURE__*/_jsx(SkeletonBlock, {
          height: "20px",
          width: "120px"
        })
      }), /*#__PURE__*/_jsx(SkeletonWrapper, {
        borderRadius: "100",
        children: /*#__PURE__*/_jsx(SkeletonBlock, {
          height: "32px",
          width: "80%"
        })
      }), /*#__PURE__*/_jsx(SkeletonWrapper, {
        borderRadius: "100",
        children: /*#__PURE__*/_jsx(SkeletonBlock, {
          height: "48px",
          width: "100%"
        })
      }), /*#__PURE__*/_jsxs(Flex, {
        gap: "300",
        marginBlockStart: "300",
        children: [/*#__PURE__*/_jsx(SkeletonWrapper, {
          borderRadius: "200",
          children: /*#__PURE__*/_jsx(SkeletonBlock, {
            height: "36px",
            width: "160px"
          })
        }), /*#__PURE__*/_jsx(SkeletonWrapper, {
          borderRadius: "200",
          children: /*#__PURE__*/_jsx(SkeletonBlock, {
            height: "36px",
            width: "120px"
          })
        })]
      })]
    }), !isMobile && !isTablet && /*#__PURE__*/_jsx(View, {
      alignSelf: "center",
      borderRadius: "300",
      flex: "1",
      overflow: "hidden",
      children: /*#__PURE__*/_jsx(ThumbnailWrapper, {
        children: /*#__PURE__*/_jsx(SkeletonWrapper, {
          borderRadius: "300",
          height: "100%",
          width: "100%",
          children: /*#__PURE__*/_jsx(SkeletonBlock, {
            height: "100%",
            width: "100%"
          })
        })
      })
    })]
  })
});
const NextStepSectionLayout = ({
  children,
  registeredEventCount,
  totalWorkshops
}) => {
  const {
    isMobile
  } = useMediaQuery();
  return /*#__PURE__*/_jsxs(OnboardingSectionLayout, {
    id: ACADEMY_ONBOARDING_SECTION_IDS.nextSteps,
    paddingBlockEnd: "600",
    paddingBlockStart: isMobile ? '400' : '1100',
    children: [/*#__PURE__*/_jsx(PageSectionHeading, {
      subtitle: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "learningCenter.academyOnboarding.nextStepSubtitle",
        options: {
          registered: registeredEventCount,
          total: totalWorkshops
        }
      }),
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.academyOnboarding.nextStepTitle"
      })
    }), children]
  });
};
const NextStepSection = () => {
  var _nextEvent$id, _registeredEvent$sess;
  const {
    isMobile,
    isTablet,
    isLaptop
  } = useMediaQuery();
  const {
    orderedIds,
    nextEvent,
    nextAvailableSession,
    totalWorkshops,
    registeredEventCount,
    isAllRegistered,
    learningEventsLoading,
    learningEventsError
  } = useNextStepEvent();
  const {
    goTo
  } = useOverviewNavigation();
  const [fallbackUrl, setFallbackUrl] = useState();
  const [registeredEvent, setRegisteredEvent] = useState(null);
  const pendingRegistrationRef = useRef(null);
  const {
    registerForSession,
    registrationLoading
  } = useRegisterForSession({
    eventId: (_nextEvent$id = nextEvent === null || nextEvent === void 0 ? void 0 : nextEvent.id) !== null && _nextEvent$id !== void 0 ? _nextEvent$id : 0,
    onCompleted: data => {
      const pending = pendingRegistrationRef.current;
      if (!pending) return;
      pendingRegistrationRef.current = null;
      setRegisteredEvent(Object.assign({}, pending, {
        session: Object.assign({}, pending.session, {
          joinUrl: data.joinUrl
        })
      }));
    },
    onFallback: setFallbackUrl,
    onError: () => {
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.liveEventOverviewModal.registrationError"
        })
      });
    }
  });
  const confettiCleanupRef = useRef(undefined);
  const hasTriggeredConfettiRef = useRef(false);
  useEffect(() => {
    if (!hasTriggeredConfettiRef.current && isAllRegistered && sessionStorage.getItem(CONFETTI_SESSION_KEY)) {
      var _confettiCleanupRef$c;
      hasTriggeredConfettiRef.current = true;
      sessionStorage.removeItem(CONFETTI_SESSION_KEY);
      (_confettiCleanupRef$c = confettiCleanupRef.current) === null || _confettiCleanupRef$c === void 0 || _confettiCleanupRef$c.call(confettiCleanupRef);
      confettiCleanupRef.current = startConfetti();
    }
  }, [isAllRegistered]);
  useEffect(() => {
    return () => {
      var _confettiCleanupRef$c2;
      (_confettiCleanupRef$c2 = confettiCleanupRef.current) === null || _confettiCleanupRef$c2 === void 0 || _confettiCleanupRef$c2.call(confettiCleanupRef);
      confettiCleanupRef.current = undefined;
    };
  }, []);

  // No configured workshop IDs for this env (e.g. prod before real IDs land).
  // Hide the section entirely rather than rendering an empty-state card.
  if (orderedIds.length === 0) {
    return null;
  }
  if (learningEventsLoading) {
    return /*#__PURE__*/_jsx(NextStepSectionLayout, {
      registeredEventCount: 0,
      totalWorkshops: orderedIds.length,
      children: /*#__PURE__*/_jsx(NextStepCardSkeleton, {
        isLaptop: isLaptop,
        isMobile: isMobile,
        isTablet: isTablet
      })
    });
  }
  if (learningEventsError) {
    return /*#__PURE__*/_jsx(ApiError, {
      error: learningEventsError,
      markerDefault: "ACADEMY_ONBOARDING_NEXT_STEP_API_ERROR",
      resetLink: ACADEMY_ONBOARDING_URL_FRAGMENT
    });
  }
  if (isAllRegistered) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "ACADEMY_ONBOARDING_NEXT_STEP_LOADED",
      children: /*#__PURE__*/_jsx(NextStepSectionLayout, {
        registeredEventCount: registeredEventCount,
        totalWorkshops: totalWorkshops,
        children: /*#__PURE__*/_jsxs(View, {
          marginBlock: "1000",
          textAlign: "center",
          children: [/*#__PURE__*/_jsx(Pictogram, {
            inlineSize: "100%",
            marginBlockEnd: "600",
            name: "CelebrationSuccess"
          }), /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.academyOnboarding.allWorkshopsRegistered"
            })
          })]
        })
      })
    });
  }
  if (!nextEvent) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "ACADEMY_ONBOARDING_NEXT_STEP_LOADED",
      children: /*#__PURE__*/_jsx(NextStepSectionLayout, {
        registeredEventCount: registeredEventCount,
        totalWorkshops: totalWorkshops,
        children: /*#__PURE__*/_jsxs(View, {
          marginBlock: "1000",
          textAlign: "center",
          children: [/*#__PURE__*/_jsx(Pictogram, {
            inlineSize: "100%",
            marginBlockEnd: "600",
            name: "EmptyStateSearch"
          }), /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.academyOnboarding.noNextStepsFound"
            })
          })]
        })
      })
    });
  }
  const handleRegister = () => {
    if (!nextAvailableSession) {
      return;
    }
    setFallbackUrl(undefined);
    pendingRegistrationRef.current = {
      session: nextAvailableSession,
      liveEventId: nextEvent.id,
      liveEventTitle: nextEvent.name
    };
    if (registeredEventCount === totalWorkshops - 1) {
      sessionStorage.setItem(CONFETTI_SESSION_KEY, '1');
    }
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'onboarding-next-step-register-click',
      contentType: 'LIVE_EVENT',
      contentId: nextEvent.id,
      contentName: nextEvent.name,
      sessionId: nextAvailableSession.id
    });
    void registerForSession(nextAvailableSession.eventSessionExternalId);
  };
  const handleShowOtherTimes = () => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'onboarding-next-step-show-other-times-click',
      contentType: 'LIVE_EVENT',
      contentId: nextEvent.id,
      contentName: nextEvent.name
    });
    goTo({
      id: nextEvent.id,
      type: 'LIVE_EVENT'
    });
  };
  const isStacked = isMobile || isTablet;
  const showOtherTimesButton = /*#__PURE__*/_jsx(Button, {
    onClick: handleShowOtherTimes,
    responsive: !isStacked,
    size: "medium",
    use: "transparent",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.academyOnboarding.showOtherTimes"
    })
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [fallbackUrl && /*#__PURE__*/_jsx(ZoomRegistrationFallbackModal, {
      fallbackUrl: fallbackUrl,
      onClose: () => setFallbackUrl(undefined)
    }), registeredEvent && /*#__PURE__*/_jsx(RegistrationConfirmationModal, {
      joinUrl: (_registeredEvent$sess = registeredEvent.session.joinUrl) !== null && _registeredEvent$sess !== void 0 ? _registeredEvent$sess : null,
      liveEventId: registeredEvent.liveEventId,
      liveEventTitle: registeredEvent.liveEventTitle,
      onClose: () => setRegisteredEvent(null),
      session: registeredEvent.session
    }), /*#__PURE__*/_jsx(NavMarker, {
      name: "ACADEMY_ONBOARDING_NEXT_STEP_LOADED",
      children: /*#__PURE__*/_jsx(NextStepSectionLayout, {
        registeredEventCount: registeredEventCount,
        totalWorkshops: totalWorkshops,
        children: /*#__PURE__*/_jsx(CardWrapper, {
          borderRadius: "300",
          marginBlockEnd: "0",
          paddingBlock: "800",
          paddingInline: isLaptop ? '400' : '800',
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: isMobile || isTablet ? 'stretch' : 'center',
            flexDirection: isMobile || isTablet ? 'column' : 'row',
            gap: "600",
            children: [(isMobile || isTablet) && nextEvent.previewThumbnailRectangleUrl && /*#__PURE__*/_jsx(View, {
              borderRadius: "300",
              overflow: "hidden",
              children: /*#__PURE__*/_jsx(Image, {
                alt: nextEvent.name,
                blockSize: "100%",
                inlineSize: "100%",
                objectFit: "cover",
                src: nextEvent.previewThumbnailRectangleUrl
              })
            }), /*#__PURE__*/_jsxs(Flex, {
              alignItems: "start",
              blockSize: "100%",
              flex: "2 1 0",
              flexDirection: "column",
              justifyContent: "center",
              children: [nextAvailableSession && /*#__PURE__*/_jsx(Text, {
                marginBlockEnd: "200",
                typography: "heading-50",
                children: /*#__PURE__*/_jsx(SessionDateDisplay, {
                  endTime: getSessionEndTime(nextAvailableSession),
                  startTime: getSessionStartTime(nextAvailableSession)
                })
              }), /*#__PURE__*/_jsx(H3, {
                marginBlockEnd: "400",
                typography: "heading-500",
                children: nextEvent.name
              }), /*#__PURE__*/_jsx(Text, {
                marginBlockEnd: "600",
                typography: "body-100",
                children: /*#__PURE__*/_jsx(ClampedText, {
                  children: nextEvent.description
                })
              }), /*#__PURE__*/_jsxs(Flex, {
                alignItems: isMobile || isTablet ? 'stretch' : 'start',
                flexDirection: isMobile || isTablet ? 'column' : 'row',
                gap: "300",
                inlineSize: isMobile || isTablet ? '100%' : undefined,
                children: [/*#__PURE__*/_jsx(LoadingButton, {
                  loading: registrationLoading,
                  onClick: handleRegister,
                  size: "medium",
                  use: "primary",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.academyOnboarding.registerForWorkshop"
                  })
                }), isStacked ? /*#__PURE__*/_jsx(Flex, {
                  justifyContent: "center",
                  children: showOtherTimesButton
                }) : showOtherTimesButton]
              })]
            }), !isMobile && !isTablet && nextEvent.previewThumbnailRectangleUrl && /*#__PURE__*/_jsx(View, {
              alignSelf: "center",
              borderRadius: "300",
              flex: "1",
              overflow: "hidden",
              children: /*#__PURE__*/_jsx(ThumbnailWrapper, {
                children: /*#__PURE__*/_jsx(Image, {
                  alt: nextEvent.name,
                  blockSize: "100%",
                  inlineSize: "100%",
                  objectFit: "cover",
                  src: nextEvent.previewThumbnailRectangleUrl
                })
              })
            })]
          })
        })
      })
    })]
  });
};
export default NextStepSection;